import React, { useEffect, useMemo, useState } from "react";
import { api, fmtPKR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  CheckCircle2, Banknote, Pencil, Hourglass, Wallet, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  approved: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

function StatCard({ icon: Icon, label, count, total, tone, testid }) {
  const tones = {
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    sky: "bg-sky-500/10 border-sky-500/30 text-sky-300",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  };
  return (
    <div data-testid={testid} className="bg-[#151A23] border border-slate-800 rounded-md p-5">
      <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular text-white">{fmtPKR(total)}</div>
      <div className="text-xs text-slate-500 mt-1 tabular">{count} entr{count === 1 ? "y" : "ies"}</div>
    </div>
  );
}

export default function AdminCommissions() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ pending: { count: 0, total: 0 }, approved: { count: 0, total: 0 }, paid: { count: 0, total: 0 } });
  const [distributors, setDistributors] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [distFilter, setDistFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [editShare, setEditShare] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [payoutFor, setPayoutFor] = useState(null);
  const [payoutMethod, setPayoutMethod] = useState("bank_transfer");
  const [payoutDate, setPayoutDate] = useState(new Date().toISOString().slice(0, 10));
  const [payoutRef, setPayoutRef] = useState("");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [bankPreview, setBankPreview] = useState(null);

  const load = () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (distFilter !== "all") params.distributor_id = distFilter;
    api.get("/admin/commissions", { params }).then((r) => setRows(r.data));
    api.get("/admin/commissions/summary").then((r) => setSummary(r.data));
  };

  useEffect(() => {
    api.get("/admin/distributors").then((r) => setDistributors(r.data));
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, distFilter]);

  const approve = async (c) => {
    try {
      await api.post(`/admin/commissions/${c.id}/approve`);
      toast.success("Commission approved");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const markPaid = async (c) => {
    // Open payout details dialog with distributor bank
    try {
      const { data } = await api.get(`/admin/distributors/${c.distributor_id}`);
      setBankPreview(data?.distributor?.bank || {});
    } catch (_e) {
      setBankPreview({});
    }
    setPayoutFor(c);
    setPayoutMethod("bank_transfer");
    setPayoutDate(new Date().toISOString().slice(0, 10));
    setPayoutRef("");
    setPayoutNotes("");
  };

  const submitPayout = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/commissions/${payoutFor.id}/settle`, {
        payment_method: payoutMethod,
        payout_date: payoutDate,
        payout_reference: payoutRef,
        notes: payoutNotes,
      });
      toast.success("Commission paid · wallet credited");
      setPayoutFor(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setEditShare(String(c.distributor_share));
    setEditNotes(c.notes || "");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (Number(editShare) !== editing.distributor_share) payload.distributor_share = Number(editShare);
      if (editNotes !== (editing.notes || "")) payload.notes = editNotes;
      if (Object.keys(payload).length === 0) {
        setEditing(null);
        return;
      }
      await api.put(`/admin/commissions/${editing.id}`, payload);
      toast.success("Commission updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const distributorOptions = useMemo(() => distributors.map((d) => ({
    id: d.id, name: d.name + (d.city ? ` · ${d.city}` : ""),
  })), [distributors]);

  return (
    <div className="space-y-6" data-testid="admin-commissions-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">Commissions</h1>
        <p className="text-slate-400 mt-1">Approve, pay out, edit distributor commissions.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Hourglass} label="Pending approval" count={summary.pending.count} total={summary.pending.total} tone="amber" testid="comm-stat-pending" />
        <StatCard icon={Sparkles} label="Approved · ready to pay" count={summary.approved.count} total={summary.approved.total} tone="sky" testid="comm-stat-approved" />
        <StatCard icon={Wallet} label="Paid out" count={summary.paid.count} total={summary.paid.total} tone="emerald" testid="comm-stat-paid" />
      </div>

      <div className="bg-[#151A23] border border-slate-800 rounded-md">
        <div className="p-4 border-b border-slate-800 flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {["all", "pending", "approved", "paid"].map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)} data-testid={`comm-filter-${f}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border capitalize ${statusFilter === f ? "bg-amber-500 border-amber-500 text-slate-900" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                {f}
              </button>
            ))}
          </div>
          <Select value={distFilter} onValueChange={setDistFilter}>
            <SelectTrigger className="h-8 w-60 bg-slate-900 border-slate-700 text-slate-200" data-testid="comm-filter-distributor">
              <SelectValue placeholder="All distributors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All distributors</SelectItem>
              {distributorOptions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-sm text-slate-500 ml-auto">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Distributor</th>
                <th className="text-left px-4 py-3 font-semibold">Shop</th>
                <th className="text-right px-4 py-3 font-semibold">Plan amount</th>
                <th className="text-right px-4 py-3 font-semibold">Distributor share</th>
                <th className="text-right px-4 py-3 font-semibold">Platform share</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Notes</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">No commissions found.</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`comm-row-${c.id}`}>
                  <td className="px-4 py-3 text-xs tabular text-slate-400">{(c.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-medium text-white">{c.distributor_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-300">{c.shop_name}{c.is_bonus_shop && <span className="ml-1.5 text-amber-400 text-[10px] font-bold">★BONUS</span>}</td>
                  <td className="px-4 py-3 text-right tabular text-white">{fmtPKR(c.plan_amount)}</td>
                  <td className="px-4 py-3 text-right tabular font-bold text-emerald-300">{fmtPKR(c.distributor_share)} <span className="text-[10px] text-slate-500 font-normal">({c.commission_rate}%)</span></td>
                  <td className="px-4 py-3 text-right tabular text-slate-400">{fmtPKR(c.platform_share)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold border ${STATUS_TONE[c.status] || ""}`}>
                      {c.status}
                    </span>
                    {c.status === "paid" && c.paid_at && (
                      <div className="text-[10px] text-slate-500 mt-0.5 tabular">paid {c.paid_at.slice(0, 10)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-[180px] truncate" title={c.notes || ""}>{c.notes || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {c.status !== "paid" && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="text-slate-300 hover:text-white" title="Edit" data-testid={`comm-edit-${c.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {c.status === "pending" && (
                        <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" onClick={() => approve(c)} data-testid={`comm-approve-${c.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                      {c.status === "approved" && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => markPaid(c)} data-testid={`comm-pay-${c.id}`}>
                          <Banknote className="w-4 h-4 mr-1" /> Mark Paid
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md bg-[#151A23] text-slate-200 border border-slate-800" data-testid="comm-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Edit commission · {editing?.shop_name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-slate-500 uppercase">Distributor</div><div className="font-semibold">{editing.distributor_name}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Plan amount</div><div className="font-semibold tabular">{fmtPKR(editing.plan_amount)}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Status</div><div className="font-semibold capitalize">{editing.status}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Rate</div><div className="font-semibold tabular">{editing.commission_rate}%</div></div>
              </div>
              <div className="space-y-1.5">
                <Label>Distributor share (PKR)</Label>
                <Input
                  type="number" min={0} max={editing.plan_amount} step="0.01"
                  value={editShare}
                  onChange={(e) => setEditShare(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-slate-200"
                  data-testid="comm-edit-share"
                />
                <div className="text-xs text-slate-500">
                  Platform share will become {fmtPKR(Math.max(0, (editing.plan_amount || 0) - Number(editShare || 0)))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Payout notes (optional)</Label>
                <Textarea
                  rows={2}
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="e.g. Adjusted for partial month, Easypaisa ref #ABC123"
                  className="bg-slate-900 border-slate-700 text-slate-200"
                  data-testid="comm-edit-notes"
                />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="comm-edit-save">Save changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!payoutFor} onOpenChange={(v) => !v && setPayoutFor(null)}>
        <DialogContent className="max-w-lg bg-[#151A23] text-slate-200 border border-slate-800" data-testid="comm-payout-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Mark as Paid · {payoutFor?.distributor_name}</DialogTitle>
          </DialogHeader>
          {payoutFor && (
            <form onSubmit={submitPayout} className="space-y-4">
              <div className="bg-slate-800/50 border border-slate-700 rounded p-3 text-sm">
                <div className="text-xs text-slate-500 uppercase font-semibold mb-2">Distributor bank details</div>
                {bankPreview && Object.keys(bankPreview).length > 0 ? (
                  <div className="space-y-1 font-mono text-xs">
                    {bankPreview.account_holder && <div><span className="text-slate-500">Holder: </span>{bankPreview.account_holder}</div>}
                    {bankPreview.bank_name && <div><span className="text-slate-500">Bank: </span>{bankPreview.bank_name}</div>}
                    {bankPreview.account_number && <div><span className="text-slate-500">A/C: </span>{bankPreview.account_number}</div>}
                    {bankPreview.branch && <div><span className="text-slate-500">Branch: </span>{bankPreview.branch}</div>}
                    {bankPreview.jazzcash_number && <div><span className="text-slate-500">JazzCash: </span>{bankPreview.jazzcash_number}</div>}
                    {bankPreview.easypaisa_number && <div><span className="text-slate-500">Easypaisa: </span>{bankPreview.easypaisa_number}</div>}
                  </div>
                ) : <div className="text-amber-300 text-xs">No bank details on file. Distributor needs to add them in their wallet page.</div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Payment method</Label>
                  <Select value={payoutMethod} onValueChange={setPayoutMethod}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200" data-testid="payout-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="jazzcash">JazzCash</SelectItem>
                      <SelectItem value="easypaisa">Easypaisa</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Payout date</Label>
                  <Input type="date" value={payoutDate} onChange={(e) => setPayoutDate(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200" data-testid="payout-date" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Reference (optional)</Label>
                <Input value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} placeholder="e.g. HBL TX#9876" className="bg-slate-900 border-slate-700 text-slate-200" data-testid="payout-ref" />
              </div>
              <div className="space-y-1.5">
                <Label>Notes (optional)</Label>
                <Textarea rows={2} value={payoutNotes} onChange={(e) => setPayoutNotes(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200" data-testid="payout-notes" />
              </div>
              <div className="text-xs text-slate-500">
                Wallet will be credited with <span className="text-emerald-300 font-semibold tabular">{fmtPKR(payoutFor.distributor_share)}</span>.
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payout-submit">Confirm payout</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DialogPlaceholder />
    </div>
  );
}

// no-op closer to keep JSX balanced after appended dialogs
function DialogPlaceholder() { return null; }
